import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/pages/home/components/Footer';

type FAQCategory = 'all' | 'shipping' | 'returns' | 'payment' | 'orders';

interface FAQ {
  q: string;
  a: string;
  category: FAQCategory;
}

const faqs: FAQ[] = [
  { category: 'shipping', q: 'Which Emirates do you deliver to?', a: 'We deliver to all 7 Emirates across the UAE: Dubai, Abu Dhabi, Sharjah, Ajman, Umm Al Quwain, Ras Al Khaimah, and Fujairah. Standard delivery takes 3-5 business days, and express next-day delivery is available for AED 35.' },
  { category: 'shipping', q: 'Is delivery free?', a: 'Yes! We offer free standard delivery on all orders over AED 200. For orders below AED 200, a flat delivery fee of AED 25 applies. Express delivery is available for AED 35 regardless of order value.' },
  { category: 'shipping', q: 'How can I track my order?', a: 'You can track your order via WhatsApp by messaging us at +971 50 000 0000 with your order number. We\'ll send you real-time updates on your delivery status. You\'ll also receive SMS notifications at each stage.' },
  { category: 'shipping', q: 'How long does standard delivery take?', a: 'Standard delivery takes 3-5 business days across all UAE Emirates. Express delivery is available for next-day delivery (order before 2PM). Remote areas in some Emirates may take an additional 1-2 days.' },
  { category: 'returns', q: 'What is your return policy?', a: 'We offer a hassle-free 7-day return policy from the date of delivery. Items must be in their original condition, unused, and in original packaging. Simply WhatsApp us at +971 50 000 0000 to initiate a return.' },
  { category: 'returns', q: 'How do I return an item?', a: 'To return an item: 1) Contact us via WhatsApp or email within 7 days of delivery. 2) We\'ll arrange a free pickup from your address. 3) Once we receive and inspect the item, your refund will be processed within 3-5 business days.' },
  { category: 'returns', q: 'Are there any items that cannot be returned?', a: 'For hygiene reasons, the following items cannot be returned once opened: skincare products, cosmetics, and personal care items. Electronics must be returned in original sealed packaging. Food items are non-returnable.' },
  { category: 'payment', q: 'Do you accept Cash on Delivery?', a: 'Yes! Cash on Delivery (COD) is our most popular payment method in the UAE. You pay the delivery person when your order arrives. COD is available for all orders across all 7 Emirates.' },
  { category: 'payment', q: 'What payment methods do you accept?', a: 'We accept: Cash on Delivery (COD), Credit/Debit Cards (Visa, Mastercard, AMEX), and Apple Pay. All online payments are secured with 256-bit SSL encryption.' },
  { category: 'payment', q: 'Is there VAT on my order?', a: 'Yes, all prices include 5% VAT as per UAE Federal Tax Authority regulations. The VAT amount is shown separately in your order summary and receipt.' },
  { category: 'payment', q: 'Are there any hidden charges?', a: 'No hidden charges! The price you see is what you pay, plus delivery fee (if applicable) and 5% VAT. We believe in complete transparency with our pricing.' },
  { category: 'orders', q: 'How do I place an order?', a: 'You can place an order directly on our website by adding items to your cart and completing checkout. Alternatively, you can order via WhatsApp at +971 50 000 0000 — just send us the product name and your delivery address.' },
  { category: 'orders', q: 'Can I modify or cancel my order?', a: 'You can modify or cancel your order within 2 hours of placing it by contacting us via WhatsApp at +971 50 000 0000. Once the order is dispatched, it cannot be cancelled but can be returned after delivery.' },
  { category: 'orders', q: 'What if I receive a damaged or wrong item?', a: 'We sincerely apologize if this happens! Please WhatsApp us at +971 50 000 0000 with a photo of the item within 24 hours of delivery. We\'ll arrange an immediate replacement or full refund at no extra cost.' },
  { category: 'orders', q: 'Do you offer bulk or wholesale orders?', a: 'Yes! We offer special pricing for bulk orders (10+ units). Contact us via WhatsApp at +971 50 000 0000 or email wholesale@aishopio.ae for a custom quote. We serve businesses across all UAE Emirates.' },
];

export default function FAQPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<FAQCategory>('all');
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    document.title = 'FAQ - Shipping, Returns & Payment | AiShopio Dubai UAE';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Frequently asked questions about AiShopio Dubai. Learn about UAE shipping, 7-day returns, cash on delivery, order tracking and more. Fast answers for UAE customers.');

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      name: 'AiShopio Dubai FAQ',
      url: `${siteUrl}/faq`,
      mainEntity: faqs.map(faq => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: { '@type': 'Answer', text: faq.a },
      })),
    };

    const existing = document.querySelector('script[data-schema="faq"]');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'faq');
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => { document.querySelector('script[data-schema="faq"]')?.remove(); };
  }, []);

  const categories: { key: FAQCategory; label: string; icon: string }[] = [
    { key: 'all', label: 'All Questions', icon: 'ri-question-line' },
    { key: 'shipping', label: 'Shipping', icon: 'ri-truck-line' },
    { key: 'returns', label: 'Returns', icon: 'ri-refresh-line' },
    { key: 'payment', label: 'Payment', icon: 'ri-bank-card-line' },
    { key: 'orders', label: 'Orders', icon: 'ri-shopping-bag-line' },
  ];

  const filtered = useMemo(() => {
    return faqs.filter(faq => {
      const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
      const matchesSearch = !searchQuery || faq.q.toLowerCase().includes(searchQuery.toLowerCase()) || faq.a.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [searchQuery, activeCategory]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50/30 via-white to-pink-50/30">
      <Header />

      {/* Hero */}
      <div className="pt-24 pb-10 px-4 sm:px-6" style={{ background: 'linear-gradient(135deg, #fff7ed, #fdf2f8)' }}>
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black bg-gradient-to-r from-orange-500 to-pink-600 bg-clip-text text-transparent mb-3">
            Frequently Asked Questions
          </h1>
          <p className="text-gray-500 mb-2">
            <Link to="/" className="hover:text-orange-500 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line mx-1"></i>
            <span className="text-gray-600">FAQ</span>
          </p>
          <p className="text-gray-500 mb-8">Find answers to common questions about shopping with AiShopio in the UAE</p>

          {/* Search */}
          <div className="relative max-w-xl mx-auto">
            <i className="ri-search-line absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
            <input
              type="text"
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setOpenIndex(null); }}
              placeholder="Search questions..."
              className="w-full pl-14 pr-6 py-4 rounded-full border border-gray-200 bg-white text-gray-800 focus:outline-none focus:border-orange-400 text-base"
            />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 justify-center mb-8">
          {categories.map(cat => (
            <button
              key={cat.key}
              onClick={() => { setActiveCategory(cat.key); setOpenIndex(null); }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                activeCategory === cat.key
                  ? 'bg-gradient-to-r from-orange-500 to-pink-600 text-white'
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-orange-400 hover:text-orange-500'
              }`}
            >
              <i className={`${cat.icon} text-base`}></i>
              {cat.label}
              <span className={`text-xs px-2 py-0.5 rounded-full ${activeCategory === cat.key ? 'bg-white/20' : 'bg-gray-100'}`}>
                {cat.key === 'all' ? faqs.length : faqs.filter(f => f.category === cat.key).length}
              </span>
            </button>
          ))}
        </div>

        {/* Results count */}
        {searchQuery && (
          <p className="text-sm text-gray-500 mb-4 text-center">
            Found <strong className="text-gray-800">{filtered.length}</strong> results for &quot;<strong className="text-orange-500">{searchQuery}</strong>&quot;
          </p>
        )}

        {/* FAQ Accordion */}
        {filtered.length === 0 ? (
          <div className="text-center py-12">
            <i className="ri-question-line text-5xl text-gray-300 mb-4 block"></i>
            <p className="text-gray-500">No questions found. Try a different search term.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((faq, i) => (
              <div
                key={i}
                className={`bg-white/70 backdrop-blur-sm rounded-2xl border transition-all duration-300 overflow-hidden ${
                  openIndex === i ? 'border-orange-400' : 'border-gray-100/50 hover:border-orange-200'
                }`}
              >
                <button
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 p-5 text-left cursor-pointer"
                >
                  <div className="flex items-start gap-3">
                    {openIndex === i && (
                      <div className="w-1 h-full min-h-[20px] rounded-full bg-gradient-to-b from-orange-500 to-pink-600 flex-shrink-0 mt-0.5" />
                    )}
                    <span className={`font-bold text-sm md:text-base transition-colors duration-300 ${openIndex === i ? 'text-orange-600' : 'text-gray-800'}`}>
                      {faq.q}
                    </span>
                  </div>
                  <i className={`ri-arrow-down-s-line text-xl flex-shrink-0 transition-transform duration-300 ${openIndex === i ? 'rotate-180 text-orange-500' : 'text-gray-400'}`}></i>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openIndex === i ? 'max-h-96' : 'max-h-0'}`}>
                  <div className="px-5 pb-5">
                    <div className="pl-4 border-l-2 border-orange-200">
                      <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA Bottom */}
        <div className="mt-12 p-8 rounded-3xl text-center" style={{ background: 'linear-gradient(135deg, #fff7ed, #fdf2f8)', border: '1px solid #fed7aa' }}>
          <h3 className="text-2xl font-black text-gray-800 mb-2">Still have questions?</h3>
          <p className="text-gray-500 mb-6">Our team is ready to help you 6 days a week!</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/971500000000"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-8 py-4 rounded-full text-white font-bold hover:scale-105 transition-all cursor-pointer whitespace-nowrap"
              style={{ background: 'linear-gradient(135deg, #25d366, #128c7e)' }}
            >
              <i className="ri-whatsapp-line text-xl"></i>
              Chat on WhatsApp
            </a>
            <Link
              to="/contact"
              className="flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-orange-500 to-pink-600 text-white font-bold hover:scale-105 hover:shadow-lg hover:shadow-orange-500/30 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-line"></i>
              Send Email
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
